import { clear } from '@/utils/localStorage';
import { removeCookies } from '@/utils/cookies';
import { request } from './api';
import { useStore } from '@/helper/pinia-auto-refs';

// 获取当前用户ID的辅助函数
const getCurrentUserId = (): string | number | null => {
    const { userState, userId } = useStore('user');
    return userId.value || userState.value?.userId || null;
};

/**
 * IAM 接口返回的字段命名不完全统一（user_id / userId），归一化成 UserState 需要的结构
 */
export const normalizeIamUser = (raw: any = {}) => {
    const nickname = raw.nickname ?? raw.chineseNickname ?? '';
    return {
        username: raw.username ?? raw.user_name ?? '',
        userId: raw.user_id ?? raw.userId ?? '',
        email: raw.email ?? '',
        mobile: raw.mobile ?? '',
        mobileRegion: raw.mobileRegion ?? raw.mobile_region ?? '',
        nickname,
        chineseNickname: raw.chineseNickname ?? nickname,
        hasPassword: !!raw.hasPassword,
        hasWeixin: !!raw.hasWeixin,
    };
};

// 查询用户信息（使用 IAM 接口）
export const getUserInfo = (isJumpLogin = true) => {
    // 统一使用本地代理路径
    const baseURL = '/flagos/api/v1';
    const url = '/auth/iam/me';

    return request(
        {
            baseURL,
            url,
            method: 'get',
        },
        isJumpLogin
    )
        .then(res => {
            return { userResponse: normalizeIamUser(res?.data) };
        })
        .catch(error => {
            // 获取用户信息失败，相当于登录失败，清除用户信息和 token
            const { clearUserInfo } = useStore('user');
            clearUserInfo();
            clear();
            removeCookies(); // 清除所有 cookie
            // 重新抛出错误，让调用方知道接口失败了
            return Promise.reject(error);
        });
};

// 验证码类型：SMS-手机短信验证码，EMAIL-邮箱验证码（不区分大小写）
// 当前登录仅支持手机号，默认使用 SMS；EMAIL 预留给后续邮箱场景
export type IamCodeType = 'SMS' | 'EMAIL';

// IAM 发送验证码接口返回的 data 结构
export interface IamSendCodeData {
    success: boolean;
    code: string;
    message: string;
    remainingCooldownSeconds?: number | null;
    sentTime?: string | null;
}

/**
 * 发送验证码（IAM 接口）
 * POST /api/v1/auth/iam/send-code
 * 注意：IAM 的业务失败（如冷却期）同样返回 HTTP 200，需同时判断响应体的 code 和 data.success
 */
export const sendIamCode = async (params: { type?: IamCodeType; target: string }) => {
    // 统一使用本地代理路径
    const baseURL = '/flagos';
    const url = '/api/v1/auth/iam/send-code';

    return request(
        {
            method: 'post',
            baseURL,
            url,
            data: {
                type: params.type ?? 'SMS', // 默认为手机号短信
                target: params.target,
            },
        },
        false // isJumpLogin: 获取验证码失败时不跳转登录页
    );
};

/**
 * 验证码登录（IAM 接口）
 * POST /api/v1/auth/iam/login
 * 当前登录仅支持手机号，type 默认 SMS；EMAIL 预留给后续邮箱场景
 */
export const iamLogin = async (params: { type?: IamCodeType; target: string; code: string }) => {
    // 统一使用本地代理路径
    const baseURL = '/flagos';
    const url = '/api/v1/auth/iam/login';

    return request(
        {
            method: 'post',
            baseURL,
            url,
            data: {
                type: params.type ?? 'SMS', // 默认为手机号短信
                target: params.target,
                code: params.code,
            },
        },
        false // isJumpLogin: 登录失败（如验证码错误 401）时不跳转登录页，避免页面刷新丢失已填内容
    );
};

// 获取手机验证码（使用 IAM 接口，默认短信方式）
export const vericode = async (phone: string) => {
    return sendIamCode({ type: 'SMS', target: phone });
};
// 获取手机验证码登录（旧接口，已改为使用 iamLogin）
export const mobile = async (data?: any) => {
    // 统一使用本地代理路径
    const baseURL = '/flagos';
    const url = '/user-srv/userLogin/smsLogin';

    return request({
        method: 'post',
        baseURL,
        url,
        data: {
            code: data.code,
            phoneNumber: data.mobile,
            clientId: 'flagos',
        },
    });
};
export const updateUserInfo = (nickname: string) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
        return Promise.reject(new Error('用户ID不存在，请先登录'));
    }
    return request({
        method: 'post',
        baseURL: '/api/data-bff',
        url: '/user/updateUserInfo',
        data: {
            chineseNickname: nickname,
        },
    }).then(res => {
        return res.data;
    });
};
// 退出
export const logout = () => {
    const { clearUserInfo } = useStore('user');
    clearUserInfo();
    clear();
    removeCookies(); // 清除所有 cookie
};

export const updateFlagOsInfo = (params: { nickname?: string; phone: string }) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
        return Promise.reject(new Error('用户ID不存在，请先登录'));
    }
    return request({
        method: 'post',
        baseURL: '/flagos/api',
        url: '/v1/users/save',
        data: params,
    });
};

// 修改用户基础信息（调用其他域名的接口）
export const modifyBaseInfo = (params: any) => {
    const currentUserId = getCurrentUserId();

    if (!currentUserId) {
        return Promise.reject(new Error('用户ID不存在，请先登录'));
    }

    // 统一使用本地代理路径
    const baseURL = '/flagos/iam';
    const url = '/user/modifyBaseInfo';

    return request({
        method: 'post',
        baseURL,
        url,
        data: params,
    });
};
